"use client";

import { useCallback, useState } from "react";
import { isValidEmail } from "../validation";
import type {
  BlockedJoinNudgeErrorCode,
  BlockedJoinNudgeStatus,
  SubmitBlockedJoinNudge,
} from "../party-blocked-joins";
import { resolveBlockedJoinNudgeErrorCode } from "../party-blocked-joins";

export interface UseBlockedJoinNudgeOptions {
  submit: SubmitBlockedJoinNudge;
  initialEmail?: string;
  initialAlreadyCaptured?: boolean;
}

export interface UseBlockedJoinNudgeResult {
  email: string;
  setEmail: (email: string) => void;
  status: BlockedJoinNudgeStatus;
  errorCode: BlockedJoinNudgeErrorCode | null;
  alreadyCaptured: boolean;
  send: () => Promise<void>;
}

export function useBlockedJoinNudge(
  options: UseBlockedJoinNudgeOptions,
): UseBlockedJoinNudgeResult {
  const {
    submit,
    initialEmail = "",
    initialAlreadyCaptured = false,
  } = options;
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState<BlockedJoinNudgeStatus>(
    initialAlreadyCaptured ? "success" : "idle",
  );
  const [errorCode, setErrorCode] = useState<BlockedJoinNudgeErrorCode | null>(
    null,
  );
  const [alreadyCaptured, setAlreadyCaptured] = useState(
    Boolean(initialAlreadyCaptured),
  );

  const send = useCallback(async () => {
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setStatus("error");
      setErrorCode("invalidEmail");
      return;
    }

    setStatus("submitting");
    setErrorCode(null);

    try {
      const result = await submit(trimmed);
      setAlreadyCaptured(result.alreadyCaptured);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorCode(resolveBlockedJoinNudgeErrorCode(err));
    }
  }, [email, submit]);

  return {
    email,
    setEmail,
    status,
    errorCode,
    alreadyCaptured,
    send,
  };
}
